import { Cable, ScrollText, BarChart3 } from 'lucide-react';
import { useInView } from '../hooks/useInView';
import type { ReactNode } from 'react';

interface Step {
  number: string;
  icon: ReactNode;
  title: string;
  description: string;
}

const steps: Step[] = [
  {
    number: '01',
    icon: <Cable className="h-8 w-8" aria-hidden="true" />,
    title: 'Connect your AI providers',
    description:
      'Point your applications to the Opair Gateway. Works as a drop-in replacement for OpenAI-compatible APIs — no code changes required.',
  },
  {
    number: '02',
    icon: <ScrollText className="h-8 w-8" aria-hidden="true" />,
    title: 'Define governance policies',
    description:
      'Create policies for data protection, cost limits, model access, and compliance requirements using the console or policy-as-code.',
  },
  {
    number: '03',
    icon: <BarChart3 className="h-8 w-8" aria-hidden="true" />,
    title: 'Monitor and automate compliance',
    description:
      'Track every AI interaction in real time. Automate responses to policy violations with event-driven workflows and generate compliance reports on demand.',
  },
];

export function HowItWorks() {
  const [headingRef, headingInView] = useInView();

  return (
    <section
      id="how-it-works"
      className="relative py-24 sm:py-32 bg-gray-950"
      aria-labelledby="how-it-works-heading"
    >
      <div
        className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          ref={headingRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headingInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="how-it-works-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
          >
            Up and running in <span className="gradient-text">minutes</span>
          </h2>
          <p className="mt-4 text-lg text-gray-400">
            Three steps to complete AI governance across your organization.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <StepCard key={step.number} step={step} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, index }: { step: Step; index: number }) {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl border border-gray-800 bg-gray-900/50 p-8 transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="mb-6 flex items-center gap-4">
        <span className="text-4xl font-bold text-indigo-500/30">{step.number}</span>
        <div className="inline-flex items-center justify-center rounded-lg bg-indigo-500/10 p-2.5 text-indigo-400">
          {step.icon}
        </div>
      </div>
      <h3 className="text-lg font-semibold text-white">{step.title}</h3>
      <p className="mt-2 text-sm text-gray-400 leading-relaxed">{step.description}</p>
    </div>
  );
}
